/**
 * Expanded detail panel inside a `ChannelDatasetCard`.
 *
 * Four sections per `feature-spec.md` §2.3:
 *   1. Golden Dataset — golden + (training, evaluation when split exists)
 *   2. Annotation Pipeline — total / in-progress / awaiting / in-adjudication
 *   3. Train / Eval Split — read-only summary (bar + ratio/method/last-split
 *      meta + hard-<10 warning). Editing lives solely on the Train / Eval
 *      Split tab — there is no Configure Split button here.
 *   4. Upload History — paginated list of upload audit rows (UH-FE).
 */

import React from 'react';
import { AlertTriangle, Star, Clock, Upload as UploadIcon } from 'lucide-react';

import { cn } from '../../../../../../lib/utils';
import { Timestamp } from '../../../../../ui/timestamp';
import type { ChannelDataset } from '../../types';
import SplitBar from '../bars/SplitBar';
import UploadHistoryTable from './UploadHistoryTable';

interface Props {
  dataset: ChannelDataset;
}

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
}> = ({ icon, title }) => (
  <div className="mb-3 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
    {icon}
    {title}
  </div>
);

const StatTile: React.FC<{
  value: React.ReactNode;
  label: string;
  toneClass?: string;
}> = ({ value, label, toneClass }) => (
  <div className="rounded-md bg-background p-3 text-center">
    <div className={cn('text-lg font-bold tabular-nums text-foreground', toneClass)}>
      {value}
    </div>
    <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
  </div>
);

const ChannelDatasetDetail: React.FC<Props> = ({ dataset }) => {
  const { stats, split, uploadCount } = dataset;
  const inProgress = stats.annotated - stats.finalised;
  const awaiting = stats.totalRecords - stats.annotated;
  const showAdjudication = stats.disputed > 0;
  const trainPct = split && (split.training + split.evaluation) > 0
    ? Math.round((split.training / (split.training + split.evaluation)) * 100)
    : 0;
  const evalPct = split ? 100 - trainPct : 0;
  const showLowTrainingWarning = split !== null && split.training < 10;

  return (
    <div className="space-y-3 border-t bg-muted/20 p-4" data-testid="channel-dataset-detail">
      {/* 1. Golden Dataset */}
      <section
        className="rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-50/40 p-4"
        data-testid="detail-section-golden"
      >
        <SectionHeader
          icon={<Star className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />}
          title="Golden Dataset"
        />
        <div
          className={cn(
            'grid gap-3',
            split ? 'grid-cols-3' : 'grid-cols-1',
          )}
        >
          <StatTile
            value={stats.finalised.toLocaleString()}
            label="In Golden Dataset"
            toneClass="text-emerald-600"
          />
          {split && (
            <>
              <StatTile
                value={split.training.toLocaleString()}
                label="Training"
                toneClass="text-primary"
              />
              <StatTile
                value={split.evaluation.toLocaleString()}
                label="Evaluation"
                toneClass="text-emerald-600"
              />
            </>
          )}
        </div>
      </section>

      {/* 2. Annotation Pipeline */}
      <section
        className="rounded-md border border-border bg-card p-4"
        data-testid="detail-section-pipeline"
      >
        <SectionHeader
          icon={<Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
          title="Annotation Pipeline"
        />
        <div
          className={cn(
            'grid gap-3',
            showAdjudication ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3',
          )}
        >
          <StatTile
            value={stats.totalRecords.toLocaleString()}
            label="Total Uploaded"
          />
          <StatTile
            value={inProgress.toLocaleString()}
            label="In Progress"
            toneClass="text-amber-600"
          />
          <StatTile
            value={Math.max(0, awaiting).toLocaleString()}
            label="Awaiting Annotation"
            toneClass="text-muted-foreground"
          />
          {showAdjudication && (
            <StatTile
              value={stats.disputed.toLocaleString()}
              label="In Adjudication"
              toneClass="text-red-600"
            />
          )}
        </div>
      </section>

      {/* 3. Train / Eval Split */}
      <section
        className="rounded-md border border-border bg-card p-4"
        data-testid="detail-section-split"
      >
        <div className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          Train / Eval Split
        </div>
        {split ? (
          <>
            <SplitBar training={split.training} evaluation={split.evaluation} size="lg" />
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              <span>{evalPct}% evaluation / {trainPct}% training</span>
              <span aria-hidden="true">·</span>
              <span className="capitalize">{split.method}</span>
              <span aria-hidden="true">·</span>
              <span>Last split: <Timestamp value={split.lastSplitAt} format="date-only" /></span>
            </div>
            {showLowTrainingWarning && (
              <div
                className="mt-3 flex items-start gap-2 rounded-r-md border-l-4 border-amber-600 bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-900"
                data-testid="low-training-warning"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
                <span>
                  Training set has fewer than 10 records. Consider using a lower
                  evaluation ratio to improve few-shot coverage.
                </span>
              </div>
            )}
          </>
        ) : (
          <p className="text-[12px] text-muted-foreground">
            No split configured yet — configure one on the Train / Eval Split tab.
          </p>
        )}
      </section>

      {/* 4. Upload History */}
      <section
        className="rounded-md border border-border bg-card p-4"
        data-testid="detail-section-uploads"
      >
        <SectionHeader
          icon={<UploadIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
          title="Upload History"
        />
        <UploadHistoryTable
          channelDatasetId={dataset.id}
          uploadCount={uploadCount}
        />
      </section>
    </div>
  );
};

export default ChannelDatasetDetail;
